import React, {FormEventHandler, forwardRef, ReactNode, useEffect, useMemo} from 'react'

import cn from 'classnames/bind'
import {observer} from 'mobx-react-lite'
import dynamic from 'next/dynamic'

import MobxCheckbox from 'components/elements/fields/MobxCheckbox'
import MobxInputQuantity from 'components/elements/fields/MobxInputQuantity/MobxInputQuantity'
import MobxRadioGroup from 'components/elements/fields/MobxRadio/MobxRadioGroup'
import MobxSelect from 'components/elements/fields/MobxSelect'
import {Col, Row} from 'components/elements/grid'
import SpinnerDots from 'components/elements/SpinnerDots'
import {
	employmentTypeOptions,
	oftenPaidOptions,
	paymentTypeOptions,
} from 'models/PayStub/PayStub.configs'
import {getOftenPaidNumberValue, posthogTrack} from 'src/utils'
import {useAuthStore, usePayStubStore} from 'src/utils/contexts/RootStore.context'

import s from './FieldsPayStubStartNew.module.scss'

const cx = cn.bind(s)

const DynamicFieldsPayStubPrevYTDNew = dynamic(
	() => import('components/blocks/forms/FormPayStubNew/FieldsPayStubPrevYTDNew'),
	{
		loading: () => <SpinnerDots size="m" position="center" />,
	},
)

interface IFieldsPayStubStartProps {
	className?: string
	children?: ReactNode | string
	onChange?: FormEventHandler<HTMLDivElement>
}

const FieldsPayStubStartNew = forwardRef<HTMLDivElement, IFieldsPayStubStartProps>(
	({className, children, ...props}, ref) => {
		const payStubStore = usePayStubStore()
		const {
			employmentType,
			paymentType,
			isHiredInPast52Weeks,
			isPrevYtd,
			payPeriodsWorked,
			oftenPaid,
		} = payStubStore

		const authStore = useAuthStore()

		useEffect(() => {
			if (!authStore.hasActiveSubscription) {
				posthogTrack(`[PAYSTUB unsubscribed] Step 2 started`)
			}
		}, [authStore.hasActiveSubscription])

		useEffect(() => {
			posthogTrack(`[PAYSTUB] Step 2 started`)
		}, [])

		/** If employment type is "contractor" the payment type can have only one options - "hourly" */
		const filteredPaymentOptions = useMemo(() => {
			if (employmentType === 'contractor') {
				return paymentTypeOptions.filter((item) => item.value === 'hourly')
			}
			return paymentTypeOptions
		}, [employmentType])

		/** If payment type is "salary" the employment type can have only one options - "employee" */
		const filteredEmploymentOptions = useMemo(() => {
			if (paymentType === 'salary') {
				return employmentTypeOptions.filter((item) => item.value === 'employee')
			}
			return employmentTypeOptions
		}, [paymentType])

		useEffect(() => {
			if (isHiredInPast52Weeks) {
				const maxPeriodWorked = Math.min(Number(payPeriodsWorked), 5)

				if (maxPeriodWorked < Number(payStubStore.payStubsNumber)) {
					payStubStore.setPayStubNumber(maxPeriodWorked.toString())
				}
			}
		}, [isHiredInPast52Weeks, payPeriodsWorked, payStubStore])

		/** Max Paystub number options will change if isHiredInPast52Weeks is "true" */
		const maxPayStubNumber = useMemo(() => {
			if (isHiredInPast52Weeks) {
				/** A limit on the number of possible document creations just to reduce the number of Symmetry requests. */
				return Math.min(Number(payPeriodsWorked), 5)
			} else {
				/** A limit on the number of possible document creations just to reduce the number of Symmetry requests. */
				return Math.min(getOftenPaidNumberValue(oftenPaid), 5)
			}
		}, [isHiredInPast52Weeks, oftenPaid, payPeriodsWorked])

		/** Creates max pay periods worked for quantity input. */
		const maxPayPeriodsWorked = useMemo(() => {
			/** A limit on the number of possible document creations just to reduce the number of Symmetry requests. */
			return Math.min(Number(getOftenPaidNumberValue(oftenPaid)), 5)
		}, [oftenPaid])

		return (
			<div className={cx('start', className)} {...props} ref={ref}>
				{children}

				<div className={cx('start--fields')}>
					{/* TODO: refactor checkboxes to be able to work with store values as booleans */}
					<MobxCheckbox
						classNameWrapper={cx('field')}
						name="autoCalculator"
						label="Auto Calculator"
						store={payStubStore}
						checked={payStubStore?.autoCalculator === 'true'}
						value={payStubStore?.autoCalculator === 'true'}
						onChange={(e) => (payStubStore.autoCalculator = e.target.checked ? 'true' : 'false')}
					/>

					<div className={cx('field')}>
						<Row>
							<Col md={6} className={cx('radio-group')}>
								<MobxRadioGroup
									name="employmentType"
									label="Employment type"
									store={payStubStore}
									options={filteredEmploymentOptions}
								/>
							</Col>

							<Col md={6} className={cx('radio-group')}>
								<MobxRadioGroup
									name="paymentType"
									label="Payment type"
									store={payStubStore}
									options={filteredPaymentOptions}
								/>
							</Col>
						</Row>
					</div>

					<MobxSelect
						variant="new"
						size="large"
						className="new"
						name="oftenPaid"
						label="How often are you paid?"
						store={payStubStore}
						wrapperClassName={cx('field')}
						options={oftenPaidOptions}
						onFocus={payStubStore.pauseCalculation}
						onBlurCapture={payStubStore.resumeCalculation}
					/>

					{/* TODO: refactor checkboxes to be able to work with store values as booleans */}
					{!isHiredInPast52Weeks && (
						<MobxCheckbox
							classNameWrapper={cx('field')}
							name="prevYtd"
							label="Enter in previous YTD calculations"
							store={payStubStore}
							checked={payStubStore?.isPrevYtd}
							value={payStubStore?.isPrevYtd}
							onChange={(e) => {
								payStubStore.prevYtdItem?.setEarningsDefaultPrevYtd()
								payStubStore.prevYtd = e.target.checked ? 'true' : 'false'
								payStubStore.prevYtdItem?.calculate()
							}}
						/>
					)}

					{/* TODO: refactor checkboxes to be able to work with store values as booleans */}
					{!isPrevYtd && (
						<MobxCheckbox
							classNameWrapper={cx('field')}
							name="hiredInPast52Weeks"
							label="I was hired in the past 52 weeks"
							store={payStubStore}
							checked={payStubStore?.hiredInPast52Weeks === 'true'}
							value={payStubStore?.hiredInPast52Weeks === 'true'}
							onChange={(e) => {
								payStubStore.hiredInPast52Weeks = e.target.checked ? 'true' : 'false'
							}}
						/>
					)}

					{/* TODO: refactor paystubs store values for quantity inputs to be numbers instead of strings */}
					{isHiredInPast52Weeks && (
						<MobxInputQuantity
							wrapperClassName={cx('field')}
							label="How many pay periods have you worked?"
							name="payPeriodsWorked"
							store={payStubStore}
							max={maxPayPeriodsWorked}
						/>
					)}

					{/* TODO: refactor paystubs store values for quantity inputs to be numbers instead of strings */}
					<MobxInputQuantity
						wrapperClassName={cx('field')}
						label="Number of paystubs needed"
						name="payStubsNumber"
						store={payStubStore}
						onQuantityChange={(paystubNumber) => payStubStore.setPayStubNumber(paystubNumber)}
						max={maxPayStubNumber}
					/>
				</div>

				{isPrevYtd && (
					<DynamicFieldsPayStubPrevYTDNew>
						<header className={cx('header')}>
							<h2 className={cx('step__title')}>2.1 Enter in previous ytd here</h2>
						</header>
					</DynamicFieldsPayStubPrevYTDNew>
				)}
			</div>
		)
	},
)

FieldsPayStubStartNew.displayName = 'FieldsPayStubStartNew'

export default observer(FieldsPayStubStartNew)
